import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Zap } from "lucide-react";

const faqs = [
  {
    category: "Career & Industry",
    questions: [
      {
        q: "What is a court reporter?",
        a: "Court reporters, also known as stenographers, are highly trained professionals who create verbatim written records of legal proceedings. They use specialized stenograph machines to capture speech at 200-300 words per minute with 95%+ accuracy. Their transcripts become the official record for trials, depositions, and hearings."
      },
      {
        q: "How much do court reporters make?",
        a: "The national median salary is $67,310 annually, but earnings vary significantly based on your specific skills, location, and work setting. State court reporters in New York earn $77,911-$102,060, while federal court reporters can earn $118,000-$135,000 base plus transcript fees. Freelance reporters who develop specialized skills have unlimited earning potential."
      },
      {
        q: "Is court reporting a good career in 2026?",
        a: "Yes! Despite a 0% projected growth rate nationally, there's actually a significant shortage of court reporters. Numbers are down 21% with only 23,000 practicing stenographers. This shortage creates excellent job opportunities and competitive salaries for those who enter the field."
      },
      {
        q: "Will AI replace court reporters?",
        a: "No. Courts require 95%+ verbatim accuracy for official records, which current AI and voice recognition technology cannot reliably achieve, especially with accents, legal terminology, multiple speakers, and courtroom acoustics. Human stenographers remain the gold standard for accuracy in legal proceedings."
      },
      {
        q: "Where do court reporters work?",
        a: "Court reporters work in various settings: official court positions (federal, state, local courts), freelance depositions for law firms, broadcast captioning for TV networks, CART services for the deaf and hard of hearing, and corporate settings."
      }
    ]
  },
  {
    category: "Training & Certification",
    questions: [
      {
        q: "How long does it take to become a court reporter?",
        a: "Most court reporting programs take 2-3 years to complete. The primary challenge is building speed—you'll progress from learning the steno machine to achieving 225 words per minute with 95% accuracy. Some students complete faster, while others take longer depending on practice dedication."
      },
      {
        q: "What certifications do court reporters need?",
        a: "The most recognized certification is the NCRA Registered Professional Reporter (RPR), which requires passing skills tests at 180/200/225 WPM with 95% accuracy. Additional certifications include Certified Realtime Reporter (CRR) and state-specific licenses."
      },
      {
        q: "Do I need a college degree to become a court reporter?",
        a: "No. Court reporting is a postsecondary nondegree profession. You need to complete an approved court reporting program and pass certification exams, but a bachelor's degree is not required. This makes it an excellent career for those who want high pay without 4+ years of college."
      },
      {
        q: "What does court reporting training involve?",
        a: "Training includes: machine shorthand (stenography), legal and medical terminology, court procedures, transcript preparation, CAT software, vocabulary building, realtime technology, and hands-on internship hours."
      }
    ]
  },
  {
    category: "StenoU Program",
    questions: [
      {
        q: "What is StenoU?",
        a: "StenoU is our comprehensive court reporting education program designed to prepare students for certification and successful careers. We focus on building both technical stenography skills and the practical knowledge needed to work in courts, depositions, or captioning."
      },
      {
        q: "How do I get started with StenoU?",
        a: "The best way to start is by booking a free consultation. We'll discuss your goals, answer questions about the program, and help you determine if court reporting is the right career path for you."
      },
      {
        q: "Do I need my own equipment?",
        a: "You'll eventually need a stenograph machine and CAT software, but we can discuss equipment options during your consultation. Some students rent or purchase used machines to start, then upgrade as they progress."
      },
      {
        q: "Can I work while attending the program?",
        a: "Many students do balance work and training. However, court reporting requires consistent daily practice, and students should be realistic about the time commitment. Those who treat this as a full-time pursuit tend to progress faster. The program can be structured to accommodate working adults, but expect that your outside commitments may need to be reduced as you advance."
      }
    ]
  },
  {
    category: "Work & Lifestyle",
    questions: [
      {
        q: "Can court reporters work remotely?",
        a: "Some court reporters do work remotely, particularly for deposition work. However, remote positions are not guaranteed and typically pay less than in-person work. Official court positions usually require in-person attendance. If 100% remote work is a priority for you, scopist editing (EditU) or transcript proofreading (ProofU) may be a better fit, as those roles are fully remote by nature."
      },
      {
        q: "What are the working hours like?",
        a: "Hours vary by position. Official court reporters typically work regular court hours (9-5, weekdays). Freelance reporters have more control over their schedules. You can choose which jobs to take and when to work."
      },
      {
        q: "What benefits do court reporters receive?",
        a: "Official court positions often include excellent benefits: health insurance, pension plans, 13+ paid holidays, 20+ vacation days, sick leave, and union membership. Freelancers can earn more but must arrange their own benefits."
      },
      {
        q: "Is court reporting stressful?",
        a: "It can be demanding. You need intense concentration during proceedings and must meet transcript deadlines. However, many reporters find it rewarding because they play a vital role in the justice system. The flexibility and income potential help offset the challenges."
      }
    ]
  }
];

export default function FAQPage() {
  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="faq-hero">
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8E8E8]/5 rounded-full blur-[150px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-[#E8E8E8] rounded-full animate-pulse-glow"></span>
                Frequently Asked Questions
              </span>
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="faq-heading"
            >
              Your <span className="gradient-text">Questions</span>, Answered
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-[#E0E0E0] leading-relaxed"
            >
              Everything you need to know about court reporting, certification, 
              and starting your career in the legal field.
            </motion.p>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20 md:py-32 bg-section-dark" data-testid="faq-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.map((section, sectionIndex) => (
            <motion.div 
              key={sectionIndex} 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: sectionIndex * 0.1 }}
              className="mb-16 last:mb-0"
            >
              <h2 
                className="text-xl sm:text-2xl font-bold tracking-tight text-[#F5F5F7] mb-8 pb-4 border-b border-[#333333]"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                <span className="text-[#38b6ff]">//</span> {section.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {section.questions.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`${sectionIndex}-${index}`}
                    className="border border-[#333333] bg-[#0D0D0D] px-6 data-[state=open]:border-[#38b6ff]/50 transition-colors"
                    data-testid={`faq-item-${sectionIndex}-${index}`}
                  >
                    <AccordionTrigger 
                      className="text-left py-6 text-[#F5F5F7] font-medium hover:text-[#38b6ff] hover:no-underline transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#E0E0E0] pb-6 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0D0D0D] relative overflow-hidden" data-testid="faq-cta">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#38b6ff]/5 rounded-full blur-[150px]"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p 
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Still Have Questions?
          </p>
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F5F7] mb-6"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Let&apos;s Talk About Your <span className="gradient-text">Future</span>
          </h2>
          <p className="text-[#E0E0E0] leading-relaxed mb-10 max-w-2xl mx-auto">
            Book a free consultation to get personalized answers and learn 
            how StenoU can launch your court reporting career.
          </p>
          <a
            href="/stenou#consultation"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = "/stenou#consultation";
            }}
          >
            <Button
              size="lg"
              className="rounded-none bg-[#38b6ff] text-[#0D0D0D] hover:bg-[#38b6ff]/90 btn-neon text-base px-8 py-6 font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="faq-cta-button"
            >
              <Zap className="w-5 h-5 mr-2" />
              Book Your Free Consultation
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
